import { inject, Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { filter, lastValueFrom, Observable } from "rxjs";
import { CallWithChatAdapterState, fromFlatCommunicationIdentifier } from "@azure/communication-react";
import { CommunicationUserIdentifier } from "@azure/communication-common";
import { AcsCredentialsFacade } from "live-lesson/shared/data-access-acs";
import { isNotNullOrUndefined } from "@pcp/core/util";
import { LiveSessionStateService } from "live-lesson/shared/data-access-call-state";
import { LessonService } from "shared/classroom/data-access";
import { AuthServiceFacade } from "@pcp/auth";
import { Endpoint } from "@pcp/http";
import { GroupCallLocator } from "@azure/communication-calling";

export type CalAndChatAdapterArgs = {
    // Props needed for the construction of the CallWithChatAdapter
    userId: CommunicationUserIdentifier;
    token: string;
    displayName: string;
    endpointUrl: string;
    locator: GroupCallLocator;

    stateChangedCallback?: (state: CallWithChatAdapterState) => void;
    activeElementElement: HTMLElement;

    pcpCallState: Observable<string | undefined>;
    lessonId: string;
    pcpToken: string;

    apiGet: <TResponse, TRequest>(url: string, data?: TRequest) => Promise<TResponse>;
    apiPost: <TResponse, TRequest>(url: string, data: TRequest) => Promise<TResponse>;
    apiPostProgress: typeof Endpoint.prototype.postWithProgressAsPromise;
    apiDelete: <TResponse, TRequest>(url: string, data?: TRequest) => Promise<TResponse>;
};

type CallWithChatAdapterServiceState = {
    callWithChatAdapterState: undefined | CallWithChatAdapterState;
    activeElementElement: HTMLElement | null;
}

@Injectable()
export class CallWithChatAdapterService extends ComponentStore<CallWithChatAdapterServiceState> {

    private readonly acsCredentialsFacade = inject(AcsCredentialsFacade);
    private readonly callStateService = inject(LiveSessionStateService);

    private readonly activeElementElement$ = this.select(state => state.activeElementElement).pipe(filter(isNotNullOrUndefined));
    private readonly liveLessonId$ = inject(LessonService).liveLessonId$.pipe(filter(isNotNullOrUndefined));

    private readonly pcpToken$ = inject(AuthServiceFacade).token$.pipe(filter(isNotNullOrUndefined));

    private readonly endpoint = inject(Endpoint);

    private readonly apiGet = <TResponse>(url: string) => lastValueFrom(this.endpoint.get<TResponse>(url, 'json'));
    private readonly apiPost = <TResponse, TRequest>(url: string, data: TRequest) => lastValueFrom(this.endpoint.post<TResponse, TRequest>(url, data, 'json'));
    private readonly apiDelete = <TResponse, TRequest>(url: string, data?: TRequest) => lastValueFrom(this.endpoint.delete<TResponse, TRequest>(url, data, 'json'));

    readonly callAndChatAdapter$: Observable<CalAndChatAdapterArgs> = this.select(
        this.acsCredentialsFacade.acsCredentials$,
        this.activeElementElement$,
        this.liveLessonId$,
        this.pcpToken$,

        (credentials, activeElementElement, lessonId, pcpToken): CalAndChatAdapterArgs => ({
            locator: {
                groupId: credentials.acsGroupId,
            },
            userId: fromFlatCommunicationIdentifier(credentials.acsUserId) as CommunicationUserIdentifier,
            token: credentials.acsToken,
            displayName: credentials.acsDisplayName,
            endpointUrl: "https://pcp-communication-services.communication.azure.com",
            activeElementElement: activeElementElement,

            stateChangedCallback: (state: CallWithChatAdapterState) => {
                // console.log("state changed:", state);
                this.#updateCallWithChatAdapterState(state);
            },

            pcpCallState: this.callStateService.state$,
            lessonId,
            pcpToken,

            apiGet: this.apiGet,
            apiPost: this.apiPost,
            apiPostProgress: this.endpoint.postWithProgressAsPromise,
            apiDelete: this.apiDelete,
        }),
    );

    /**
     * AzureCommunicationServices Call State.
     */
    readonly callState$ = this.select(this.state$, state => state.callWithChatAdapterState?.call?.state);

    readonly page$ = this.select(this.state$, state => state.callWithChatAdapterState?.page);

    /**
     * True if the user is connected to the call.
     * Determined by the ACS adapter state.
     */
    readonly isConnectedToCall$ = this.select(
        this.callState$,
        callState => callState === 'Connected'
    );

    constructor() {
        super({
            callWithChatAdapterState: undefined,
            activeElementElement: null,
        });
    }

    setActiveElementElement = this.updater((
        state,
        activeElementElement: HTMLElement | null
    ): CallWithChatAdapterServiceState => ({
        ...state,
        activeElementElement,
    }));

    #updateCallWithChatAdapterState = this.updater((
        state,
        callWithChatAdapterState: CallWithChatAdapterState
    ): CallWithChatAdapterServiceState => ({
        ...state,
        callWithChatAdapterState,
    }));
}
